<template>
	<div class="guest-registration">
		<p class="guest-registration__title text-center font-gilroy font-bold mb-4">{{ formRef.Title ?? formRef.Name }}</p>
		<div class="app-styled-form">
			<div ref="mountFormHere"></div>
		</div>
	</div>
</template>

<script lang="ts">
	import type { PropType } from 'vue';
	import { defineComponent, onMounted, onUnmounted, ref } from 'vue';
	import notify from '@cognitoforms/shared-components/src/utilities/notify';
	import { injectPublicFormApi, removePublicFormApi } from 'src/views/admin/utils/public-api';
	import { EmbedContext, FormOwner } from '../../../../client/src/framework/public/form-handle';
	import type { GuestRegistrationResult } from '@cognitoforms/types/server-types/services/infrastructure/authentication/guest-registration-result';
	import type { FormRef } from '@cognitoforms/types/server-types/forms/configuration/form-ref';
	import type { AfterSubmitEvent, CreateToastMessageEvent } from '../../../../client/src/framework/public/events';
	import type { FormHandle } from '../../../../client/src/framework/public/form-handle';

	export type GuestRegistrationSubmitEvent = {
		isActive: boolean;
		confirmationMessage: string;
	};

	export default defineComponent({
		components: {},
		props: {
			authResult: {
				type: Object as PropType<GuestRegistrationResult>,
				required: true
			},
			formRef: {
				type: Object as PropType<FormRef>,
				required: true
			}
		},
		emits: ['submit'],
		setup(props, { emit }) {
			let entryAdapter: FormHandle;

			const afterSubmit = (event: AfterSubmitEvent) => {
				emit('submit', {
					isActive: event.data.guestResult.isActive,
					confirmationMessage: event.data.convertedMessage
				} as GuestRegistrationSubmitEvent);
			};

			const createToastMessage = (event: CreateToastMessageEvent) => {
				notify(event.data.toastMessage, event.data.toastType);
			};

			const mountFormHere = ref<HTMLElement | null>(null);
			const mountForm = async () => {
				await injectPublicFormApi(props.authResult.FormClientApiKey, mountFormHere.value);
				entryAdapter = window.PublicForm.mount(props.formRef.Id, mountFormHere.value, FormOwner.Cognito, EmbedContext.GuestRegistration);
				entryAdapter.on('afterSubmit', afterSubmit);
				entryAdapter.on('createToastMessage', createToastMessage);

				// Add single-child class if the form has only one action button
				entryAdapter.on('ready', () => {
					document.querySelectorAll('.cog-page__navigation').forEach(nav => {
						if (nav.children.length === 1)
							nav.classList.add('single-child');
					});
				});
			};

			onMounted(mountForm);
			onUnmounted(removePublicFormApi);

			return {
				mountFormHere
			};
		},
		head() {
			return {
				htmlAttrs: {
					class: ['guest-registration']
				}
			};
		}
	});
</script>

<style lang="scss" src="@cognitoforms/styles/app/cognito-form.scss"></style>
<style lang="scss">
:root:root:root.guest-registration {

	.faux-modal__body {
		position: relative;
		z-index: 0;
		max-width: 1000px;
		padding-right: min(60px, 6vw);
		padding-left: min(60px, 6vw);
	}

	.faux-modal__wrapper {
		position: relative;
		z-index: 0;
		max-width: initial;
	}

	.guest-registration__title {
		font-size: 28px;
	}

	.cog-page__navigation.single-child {
		justify-content: center !important;
	}

	.cog-page__navigation.single-child button {
		min-width: 240px !important;
		font-size: var(--text-4) !important;
	}
}
</style>